import React, { ChangeEvent, useEffect, useState } from 'react';
import useHandleAction from '../../../../../lib/hooks/useHandleAction';
import { ConfirmDialog } from '../../../../common/ConfirmDialog/ConfirmDialog';
import { useTeamMembersContext } from '../../../../../lib/context/TeamMembersContext';
import { CommonTeamDialogType, SubteamDialogProps, TeamAccordionItemAction } from '../TeamAccordionItem.types';
import Input from '../../../../common/Input/Input';
import { SUBTEAM_NAME_MAX_LENGTH } from '../../../../../lib/constants/globals';
import { useCreateSubteamAndRenameDialogProps } from '../../../../../lib/hooks/useCreateSubteamAndRenameDialogProps';
import Notification, { NotificationType } from '../../Notification/Notification';
import styles from '../TeamAccordionItem.module.scss';

const CreateSubTeamAndRenameDialog = ({
  onClose,
  teamMeta,
  type,
}: SubteamDialogProps & { type: CommonTeamDialogType }) => {
  const { refreshTeamsSummary } = useTeamMembersContext();
  const [name, setName] = useState(type === TeamAccordionItemAction.RENAME_TEAM ? teamMeta.name ?? '' : '');
  const dialogsCommonProps = useCreateSubteamAndRenameDialogProps({ type, teamMeta });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { isPending, handleAction } = useHandleAction({
    ...dialogsCommonProps.actionProps,
    onSuccess: () => {
      refreshTeamsSummary();
      onClose();
    },
    customOnError: setErrorMessage,
  });
  const validated = name.length > 0 && !errorMessage?.length && teamMeta.name !== name;
  const confirmButtonStatus = (() => {
    if (isPending) {
      return 'loading';
    }

    return validated ? 'enabled' : 'disabled';
  })();

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (newValue.length <= SUBTEAM_NAME_MAX_LENGTH) {
      setName(event.target.value);
    }
  };

  useEffect(() => {
    setErrorMessage(null);
  }, [name]);

  return (
    <ConfirmDialog
      onCancel={onClose}
      confirmButtonStatus={confirmButtonStatus}
      onConfirm={() => handleAction(name.trim())}
      isOpen
      {...dialogsCommonProps}
    >
      <Input value={name} onChange={handleInputChange} type="text" />
      {!!errorMessage && (
        <Notification
          content={errorMessage}
          type={NotificationType.Error}
          showCloseButton={false}
          wrapperClass={styles.errorsWrapper}
          contentClass={styles.errorsContent}
          transitionAnimation
          open
          ariaRole="alert"
        />
      )}
    </ConfirmDialog>
  );
};

export default CreateSubTeamAndRenameDialog;
